// Signal Score: a 0-10 "how much does this matter" number rendered on every card, so the
// motto "pure signal, no noise" becomes something you can see. This is a deterministic,
// client-side heuristic for the v2 site (magnitude x corroboration x depth x concreteness);
// the production pipeline will later compute and store an authoritative score per card, and
// this util becomes the display fallback. No randomness, so a card always shows the same score.

type ScorableCard = {
  category?: string;
  headline?: string;
  essence?: string;
  body?: string;
  supportingSources?: unknown[];
  deepDive?: unknown;
  deep_dive?: unknown;
  isEditorsPick?: boolean;
  publishedAt?: string;
  contentType?: string;
};

// Significance leans on consequence: policy, money and science shape more lives than
// lifestyle or sport. These weights only tilt the score, they never dominate it.
const CATEGORY_WEIGHT: Record<string, number> = {
  "Power & Policy": 1.0,
  "Money & Work": 0.92,
  "Science & Systems": 0.9,
  "Planet & Environment": 0.86,
  "Technology & Tools": 0.8,
  "Culture & Curiosity": 0.62,
  "Sport & Play": 0.5,
};

// One weighed input into the score. `points` is the signed contribution (already applied);
// `detail` is the plain-language reason shown in the Verify window.
export type SignalFactor = { label: string; detail: string; points: number };

export type SignalBreakdown = {
  score: number;
  band: string;
  base: number;
  factors: SignalFactor[];
};

// The single source of truth for the score AND its explanation. computeSignalScore is a thin
// wrapper so the number a card shows always matches the breakdown the Verify window explains.
export function explainSignalScore(card: ScorableCard): SignalBreakdown {
  const base = 5.0;
  let s = base;
  const factors: SignalFactor[] = [];
  const add = (label: string, detail: string, points: number) => {
    const rounded = Math.round(points * 100) / 100;
    s += rounded;
    factors.push({ label, detail, points: rounded });
  };

  // Magnitude / scale: the category's reach.
  const cat = card.category ?? "";
  const weight = CATEGORY_WEIGHT[cat] ?? 0.72;
  add(
    "Magnitude",
    cat ? `${cat} stories reach ${weight >= 0.9 ? "a wide" : weight >= 0.7 ? "a broad" : "a narrower"} audience.` : "General significance.",
    weight * 1.7
  );

  // Credibility via corroboration: more independent sources backing the claim.
  const backing = Array.isArray(card.supportingSources) ? card.supportingSources.length : 0;
  const sources = 1 + backing;
  add(
    "Corroboration",
    `${sources} independent ${sources === 1 ? "source backs" : "sources back"} the claim.`,
    Math.min(sources, 4) * 0.4
  );

  // Depth: a story with a mapped deep dive carries more than a single beat.
  if (card.deepDive || card.deep_dive) add("Depth", "Has a mapped deep dive, not just a single beat.", 0.55);
  if (card.isEditorsPick) add("Editor's pick", "Hand-picked as one of the day's strongest.", 0.45);

  // Concreteness: a headline with a real figure or proper noun tends to be more substantive
  // than a vague one (a cheap proxy for "potential / specificity").
  const hl = card.headline ?? "";
  if (/\d/.test(hl)) add("Concreteness", "Headline carries a hard figure.", 0.3);
  if (/[A-Z][a-z]+\s[A-Z][a-z]+/.test(hl)) add("Specificity", "Names a specific entity, not a vague actor.", 0.15);

  // Freshness: a genuinely recent development edges up.
  const ts = Date.parse(card.publishedAt ?? "");
  if (Number.isFinite(ts) && Date.now() - ts < 12 * 3_600_000) add("Freshness", "Broke within the last 12 hours.", 0.3);

  // Knowledge / evergreen cards are valuable but not "breaking significance".
  if (card.contentType && card.contentType !== "news") add("Evergreen", "Reference knowledge, not a breaking development.", -0.4);

  const raw = Math.round(s * 10) / 10;
  const score = Math.max(3.6, Math.min(9.6, raw));
  return { score, band: signalBand(score), base, factors };
}

export function computeSignalScore(card: ScorableCard): number {
  return explainSignalScore(card).score;
}

// A calm, one-word read on the score, for the chip's title/aria (never shown as a grade
// that shames a card, just a quiet label).
export function signalBand(score: number): string {
  if (score >= 8.5) return "world-shaping";
  if (score >= 7.5) return "high signal";
  if (score >= 6.3) return "worth knowing";
  return "lighter read";
}
